/**
 * People whose messages you would rather not read.
 *
 * A personal list, kept in this browser and never sent anywhere. That is the
 * whole design decision worth explaining, because the server has a moderation
 * model already and this is deliberately not part of it: a kick or a ban is an
 * operator deciding what a room may see, and needs to be, because it applies to
 * everybody. Hiding somebody is one reader deciding what *they* want to read. It
 * requires no permission, affects nobody else, needs no audit trail, and the
 * person hidden is not told - all of which follow from it never leaving the
 * browser that set it.
 *
 * Filed under the nickname folded to lower case, which is the identity the
 * server uses too - its uniqueness key is exactly the display form lowercased,
 * see `nicknameKey` in src/util/nickname.js - so `Alice` and `alice` are one
 * person here as well as there. The display form is stored alongside it so the
 * list can be shown back the way the name is actually written.
 *
 * The consequence of keying on a nickname is worth being clear about: a guest
 * who picks a new nickname is a new person to this list. There is nothing to be
 * done about that from the client, and it is the same trade every chat client
 * that has ever had an ignore list has made.
 *
 * Not scoped to whoever is signed in, unlike the room list next door. A room
 * list is scoped because a hidden room's name is the way into it, so leaving one
 * behind hands the next person a key. A hide list is a reading preference like
 * the theme or the per-person volumes, and those are per browser.
 */
const STORAGE_KEY = 'webchat.hidden';

/**
 * How many people to remember. Generous - nobody has an ignore list this long -
 * and only here so that a browser's storage cannot be filled by a script
 * clicking Hide in a loop. The least recently added go first.
 */
const LIMIT = 500;

/**
 * The identity a nickname is filed under: no leading #, trimmed, lower case.
 *
 * Trimmed before the `#` is stripped as well as after, because a name pasted
 * with a leading space would otherwise keep its hash - the anchor would not be
 * at the start of the string - and file the same person twice.
 */
export function hiddenKey(nickname) {
  return String(nickname ?? '')
    .trim()
    .replace(/^#+/, '')
    .trim()
    .toLowerCase();
}

function read() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return stored;
  } catch {
    // Unreadable or corrupt: an empty list, rather than letting a bad entry
    // stop the transcript from rendering.
    return {};
  }
}

function write(entries) {
  try {
    const keys = Object.keys(entries);
    if (keys.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    // Trimmed oldest-first when over the cap.
    if (keys.length > LIMIT) {
      const kept = keys
        .sort((a, b) => (entries[a].at ?? 0) - (entries[b].at ?? 0))
        .slice(keys.length - LIMIT);
      const next = {};
      for (const key of kept) next[key] = entries[key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage denied or full. The list still works for this page; it is simply
    // not remembered, which is a better outcome than refusing to hide anybody.
  }
}

/** Whether this nickname's messages should be kept out of the transcript. */
export function isUserHidden(nickname) {
  const key = hiddenKey(nickname);
  return key !== '' && Object.hasOwn(read(), key);
}

/**
 * Everyone on the list, most recently added first, in display form.
 *
 * Ties are broken by insertion order, reversed. Two people hidden in the same
 * millisecond - which is what happens when a list is cleared and rebuilt, or a
 * script does it - would otherwise come back in the order they were added,
 * which is the wrong way round for a list that says "most recent first".
 */
export function hiddenUsers() {
  const entries = read();
  return Object.entries(entries)
    .map(([key, value], index) => ({
      key,
      nickname: value.nickname ?? key,
      at: value.at ?? 0,
      index,
    }))
    .sort((a, b) => b.at - a.at || b.index - a.index)
    .map(({ key, nickname, at }) => ({ key, nickname, at }));
}

export function hideUser(nickname) {
  const key = hiddenKey(nickname);
  if (!key) return hiddenUsers();
  const entries = read();
  // `at` is refreshed on a repeat so that re-hiding somebody moves them to the
  // front of the list rather than leaving them at the back to be trimmed first.
  entries[key] = { nickname: String(nickname).trim(), at: Date.now() };
  write(entries);
  return hiddenUsers();
}

export function unhideUser(nickname) {
  const key = hiddenKey(nickname);
  const entries = read();
  if (!Object.hasOwn(entries, key)) return hiddenUsers();
  delete entries[key];
  write(entries);
  return hiddenUsers();
}

/** Hides or unhides, and says which it did. */
export function toggleUser(nickname) {
  const hidden = isUserHidden(nickname);
  if (hidden) unhideUser(nickname);
  else hideUser(nickname);
  return { hidden: !hidden };
}

export function clearHidden() {
  write({});
  return [];
}

export default {
  hiddenKey,
  isUserHidden,
  hiddenUsers,
  hideUser,
  unhideUser,
  toggleUser,
  clearHidden,
};
